import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { createConsumer } from "@rails/actioncable"
import { canvasResize, PHOTO_THUMBNAIL, BLUR_PLACEHOLDER } from "../lib/image_resize"
import { csrfToken, postJson } from "../lib/http"
import { uploadThumbnailsInChunks, directUploadImage } from "../lib/thumbnail_backfill"

// Bridge controller on the discoveries feed + match page (native only).
//
// 1. Display: replaces [data-client-ref] placeholders with the actual photo from
//    THIS device's library (the only place that photo can come from live).
// 2. Backfill: uploads thumbnails for this user's MATCHED photos to the DB so the
//    OTHER person can see them — proactively when matching completes (MatchingChannel
//    push) and as a catch-up on connect. Matched photos only, never the whole library.
//
// Does nothing on web (BridgeComponent.shouldLoad returns false without a bridge).
export default class extends BridgeComponent {
  static component = "photo-library"

  connect() {
    super.connect()
    setTimeout(() => this._loadAll(), 150)

    // Infinite scroll appends more cards AFTER connect() (via Turbo stream). Stimulus
    // doesn't re-run connect() for appended children and there's no other hook, so the
    // initial _loadAll() would never see those later pages — their device-sourced
    // photos stay blank. Watch for newly-added [data-client-ref] placeholders and run
    // the same fill-in pass over them. Re-running _loadAll is safe: a filled
    // placeholder becomes an <img> with no data-client-ref, so it's never re-found,
    // and _fetchPhoto caches per identifier (no duplicate native fetches). The <img>
    // elements we insert have no data-client-ref, so they don't re-trigger us.
    this._appendObserver = new MutationObserver((mutations) => {
      const hasNewPlaceholders = mutations.some((m) =>
        Array.from(m.addedNodes).some((n) =>
          n.nodeType === Node.ELEMENT_NODE &&
          (n.matches?.("[data-client-ref]") || n.querySelector?.("[data-client-ref]"))
        )
      )
      if (!hasNewPlaceholders) return
      // Coalesce the burst of mutations from one appended page into a single pass.
      clearTimeout(this._appendTimer)
      this._appendTimer = setTimeout(() => this._loadAll(), 50)
    })
    this._appendObserver.observe(this.element, { childList: true, subtree: true })

    // When someone reveals (discovery-card#approve), upload THAT photo's clear image
    // right now — the background prep may not have finished it, and a group reveal has
    // no other way to get it onto S3 (others would see "developing…"). See _uploadNow.
    this._onUploadNow = (e) => this._uploadNow(e.detail?.clientRefs || [])
    document.addEventListener("photo-fetch:upload-now", this._onUploadNow)

    // Catch-up now (in case matching already finished), then stay subscribed so a
    // match created while we're here triggers the backfill immediately.
    this._backfillMatched()
    this._consumer = createConsumer()
    this._subscription = this._consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      { received: (data) => { if (data?.type === "matching_complete") this._backfillMatched() } }
    )
  }

  disconnect() {
    this._appendObserver?.disconnect()
    clearTimeout(this._appendTimer)
    document.removeEventListener("photo-fetch:upload-now", this._onUploadNow)
    this._subscription?.unsubscribe()
    this._consumer?.disconnect()
    super.disconnect()
  }

  // Upload the clear image for specific photos immediately, at high priority. Fired
  // on reveal so the just-revealed photo lands on S3 now instead of whenever the
  // best-effort background pass gets to it. No-op for already-uploaded photos (the
  // caller only sends refs whose cell is still a device placeholder).
  async _uploadNow(clientRefs) {
    for (const ref of clientRefs) {
      if (!ref || ref.startsWith("web-")) continue
      const src = await this._fetchPhoto(ref, { priority: "high", timeout: 20000 })
      if (src) this._uploadThumbnail(ref, src)
    }
  }

  // ── Display: swap placeholders with device photos ─────────────────────────

  _loadAll() {
    const groups = {}
    this.element.querySelectorAll("[data-client-ref]").forEach(el => {
      const ref = el.dataset.clientRef
      if (!ref) return
      if (!groups[ref]) groups[ref] = []
      groups[ref].push(el)
    })

    Object.entries(groups).forEach(([localIdentifier, elements]) => {
      this._fetchAndApply(localIdentifier, elements)
    })
  }

  async _fetchAndApply(localIdentifier, elements) {
    // Skip web-uploaded photos — they're not in the device photo library.
    if (localIdentifier.startsWith("web-")) return

    // High priority: this photo is on screen right now, so it jumps ahead of the
    // background prep in the fetch queue.
    const src = await this._fetchPhoto(localIdentifier, { priority: "high" })
    if (!src) return

    elements.forEach(el => {
      if (!el.isConnected) return
      const img = document.createElement("img")
      img.src             = src
      img.style.width     = "100%"
      img.style.height    = "100%"
      img.style.objectFit = "cover"
      img.style.display   = "block"
      el.replaceWith(img)
    })

    this._uploadThumbnail(localIdentifier, src)
  }

  // Fetch one photo from this device, as a data URL (or null). Serialized and
  // deduped: the Hotwire bridge keys replies by event name, so two concurrent
  // "fetchPhoto" sends clobber each other's callback (the display fetch and the
  // thumbnail-backfill fetch were racing — one callback never fired, leaving a
  // permanent camera icon). One send at a time, one fetch per identifier.
  // Fetches run one at a time (the native bridge can't handle concurrent fetchPhoto
  // calls), but through two priority lanes: "high" for photos the user is looking at
  // right now (the display swap), "low" for the background prep. A high request jumps
  // ahead of all not-yet-started low work, so on-screen photos don't wait behind the
  // backfill. If a photo is already queued low and then scrolls into view, it's
  // promoted instead of fetched twice (same cache key).
  _fetchPhoto(localIdentifier, { fast = false, timeout = 8000, priority = "low" } = {}) {
    this._cache ||= {}
    this._tasks ||= {}
    // Key by mode: the fast (blur) and high-quality fetches return different images
    // for the same identifier, so they must not share a cache slot.
    const key = (fast ? "fast:" : "") + localIdentifier
    if (key in this._cache) {
      if (priority === "high") this._promote(key)
      return this._cache[key]
    }

    const task = { key, priority, started: false, run: null }
    const result = new Promise((resolve) => {
      task.run = () => new Promise((done) => {
        task.started = true
        let finished = false
        const finish = (value) => { if (!finished) { finished = true; resolve(value); done() } }
        // Don't let a dropped reply stall the queue. The high-quality path may pull a
        // full image from iCloud, so its caller passes a longer timeout.
        setTimeout(() => finish(null), timeout)
        this.send("fetchPhoto", { localIdentifier, fast }, (response) => {
          const base64   = response?.data?.base64   || response?.base64
          const mimeType = response?.data?.mimeType || response?.mimeType || "image/jpeg"
          finish(base64 ? `data:${mimeType};base64,${base64.replace(/\s/g, "")}` : null)
        })
      })
      this._tasks[key] = task
      this._enqueue(task)
    })

    this._cache[key] = result
    // Don't remember failures: a timed-out / iCloud-stalled fetch should be retryable
    // on the next pass rather than cached as "no photo".
    result.then((v) => { delete this._tasks[key]; if (!v) delete this._cache[key] })
    return result
  }

  _enqueue(task) {
    this._highQueue ||= []
    this._lowQueue ||= []
    ;(task.priority === "high" ? this._highQueue : this._lowQueue).push(task)
    this._pump()
  }

  // Bump a still-queued background fetch into the high lane (a photo scrolled into view).
  _promote(key) {
    const task = this._tasks?.[key]
    if (!task || task.started || task.priority === "high") return
    task.priority = "high"
    const i = this._lowQueue?.indexOf(task) ?? -1
    if (i !== -1) { this._lowQueue.splice(i, 1); this._highQueue.push(task) }
  }

  async _pump() {
    if (this._pumping) return
    this._pumping = true
    try {
      while (this._highQueue?.length || this._lowQueue?.length) {
        const task = (this._highQueue.length ? this._highQueue : this._lowQueue).shift()
        await task.run()
      }
    } finally {
      this._pumping = false
    }
  }

  // ── Backfill: upload this user's matched-photo thumbnails to the DB ────────

  async _backfillMatched() {
    try {
      const res = await fetch("/library_photos/match_missing_thumbnails", {
        headers: { "X-CSRF-Token": csrfToken() }
      })
      if (!res.ok) return
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchThumbnail(ref), {
        // Pass 1 (blur): iOS fast image path — quick + reliable even for iCloud
        // photos, so every match gets a blurred preview without stalling on the
        // slow high-quality pass.
        fetchBlurSource: (ref) => this._fetchPhoto(ref, { fast: true, timeout: 8000 })
      })
    } catch (e) {
      // Non-fatal — thumbnails are cosmetic.
    }
  }

  // Fetch a photo from this device and return a resized thumbnail data URL, or null.
  async _fetchThumbnail(localIdentifier) {
    // High-quality clear image (shown after reveal) — allow longer for iCloud pulls.
    const src = await this._fetchPhoto(localIdentifier, { timeout: 20000 })
    if (!src) return null
    return canvasResize(src, PHOTO_THUMBNAIL)
  }

  async _uploadThumbnail(clientRef, dataUrl) {
    // Save the blur on its own first, so the other person gets a blurred preview
    // (not the off-white "Not revealed yet" lock) even if the S3 image upload below
    // fails. The blur is a tiny base64 column — no S3 involved.
    const blur = await canvasResize(dataUrl, BLUR_PLACEHOLDER)
    if (blur) {
      postJson("/library_photos/thumbnails", {
        thumbnails: [{ client_ref: clientRef, blur }]
      }).catch(() => {})
    }

    const thumbnail = await canvasResize(dataUrl, PHOTO_THUMBNAIL)
    if (!thumbnail) return
    try {
      // Image goes straight to S3 (direct upload); the server only gets the blob id.
      const signedId = await directUploadImage(thumbnail, clientRef)
      postJson("/library_photos/thumbnails", {
        thumbnails: [{ client_ref: clientRef, signed_id: signedId, blur }]
      }).catch(() => {})
    } catch (e) {
      console.warn("[photo-fetch] direct upload failed for", clientRef, e)
    }
  }
}
